import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.summary.addEventListener('keydown', this.#handleSummaryKeydown, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Smooth-scroll to the element whose id matches data-scroll-to-id (e.g. Judge.me reviews).
   */
  #scrollToDatasetTarget() {
    const scrollToId = this.dataset.scrollToId;
    if (!scrollToId) return;

    const rawOffset = this.dataset.scrollToOffset;
    const parsed = Number.parseInt(String(rawOffset), 10);
    const offsetPx = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;

    const tryScroll = () => {
      const target = document.getElementById(scrollToId);
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - offsetPx;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return true;
      }
      return false;
    };

    if (!tryScroll()) {
      setTimeout(tryScroll, 400);
    }
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    if (this.dataset.scrollToId) {
      event.preventDefault();
      this.#scrollToDatasetTarget();
      return;
    }

    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    if (this.dataset.scrollToId) {
      this.details.open = false;
      return;
    }

    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  /**
   * Avoid native summary activation opening details when this row only scrolls elsewhere.
   * @param {KeyboardEvent} event
   */
  #handleSummaryKeydown = (event) => {
    if (!this.dataset.scrollToId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#scrollToDatasetTarget();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
